// Mock data for elevante.ai website — strictly from Figma design

export const navLinks = [
  { label: "For Enterprises", href: "/for-enterprises" },
  { label: "For Educational institution", href: "/educational-institution" },
  { label: "Platform Features", href: "/#platform-section" },
  { label: "About", href: "/about" },
];

export const heroData = {
  heading: "The AI learning platform for",
  rotatingWords: [
    { text: "Educators", color: "#8B5CF6" },
    { text: "Students", color: "#10B981" },
    { text: "Enterprises", color: "#06B6D4" },
  ],
  subtitle: "Everything you need to learn and apply AI.",
  ctaPrimary: { label: "Book a demo", href: "#demo" },
  ctaSecondary: { label: "See in action", href: "#action" },
  tags: ["AI Labs", "Industry Simulations", "AI-Native LMS"],
};

export const featureCards = [
  {
    id: 1,
    title: "The AI skills gap is real",
    description:
      "87% of companies say they have or expect AI skills gaps. Most training programs haven't adapted fast enough.",
    stat: "87%",
    statLabel: "of companies report AI skills gaps",
  },
  {
    id: 2,
    title: "Traditional training isn't working",
    description:
      "Passive videos and theory-only courses lead to low completion rates and minimal real-world application.",
    stat: "12%",
    statLabel: "average completion rate",
  },
  {
    id: 3,
    title: "Demand for AI talent is surging",
    description:
      "AI job postings have grown 3.5x in the last 3 years. Organizations need scalable ways to build AI fluency.",
    stat: "3.5x",
    statLabel: "growth in AI job postings",
  },
];

export const problemSection = {
  heading: "AI is changing work faster than people are being prepared for it.",
  cards: [
    {
      title: "Relevance is missing for disciplines, roles, and industries",
      description:
        "Generic AI skilling is often too broad to translate into role- and industry-specific outcomes. Learners need skilling that is aligned to their domain.",
      chartType: "line",
    },
    {
      title: "Hands-on AI learning is hard and expensive to deliver at scale",
      description:
        "Practical learning often needs lab environments, infrastructure, and classroom-ready workflows. That makes delivery expensive and operationally heavy across cohorts.",
      chartType: "bar",
    },
  ],
};

export const platformSection = {
  heading: "One platform to learn, practice and apply AI.",
  subtitle:
    "From guided theory to interactive labs and real-world simulations — everything learners and instructors need, in one place.",
  tabs: [
    {
      label: "Visual Builder",
      title: "No-Code AI Workflows",
      description:
        "Drag-and-drop AI pipelines for ML, NLP, Computer Vision, and GenAI — accessible to every learner regardless of coding experience.",
    },
    {
      label: "AI Studio",
      title: "Practice with Real Tools",
      description:
        "Integrated environments with datasets, models, and assessment tools that mirror industry-standard AI workflows.",
    },
  ],
};

export const builtForFuture = {
  heading: "Built for Future of Learning",
  subtitle:
    "Designed to enable practical AI skilling across academic institutions and modern enterprises.",
  cards: [
    {
      title: "For Universities & Institutions",
      description:
        "Curriculum-aligned, instructor-led, hands-on AI learning across every department.",
      href: "#universities",
    },
    {
      title: "For Enterprises & Teams",
      description:
        "Workflow-based labs that map to real business use cases and build AI-capable teams at scale.",
      href: "#enterprises",
    },
  ],
};

export const benefitsSection = {
  heading: "Real Benefits That Go Beyond Learning",
  subtitle:
    "From building future-ready talent to improving business productivity, Elevante enables outcomes that matter.",
  features: [
    {
      id: 1,
      title: "Interactive AI Labs",
      description:
        "Hands-on AI environments where learners practice using real datasets, workflows, and tools, enabling them to move from conceptual understanding to practical application across disciplines.",
      bullets: [
        "Enables learners to <strong>drag, drop, and experiment with models, recipes, and datasets</strong> in an intuitive lab environment",
        "Allows learners to <strong>change parameters, test different approaches, and instantly see how results</strong> and outputs are affected",
        "Gives institutions a way to offer AI workflows without requiring heavy infrastructure or complex tools",
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/j7x3s2pt_Frame%202147258425.png",
    },
    {
      id: 2,
      title: "AI Course Library Across Domains and Industries",
      description:
        "A curated library of 50+ AI courses across industries, functions, and disciplines, designed to help institutions build relevant and future-ready learning pathways.",
      bullets: [
        "Gives institutions access to <strong>courses across industries, functions, and academic disciplines</strong>",
        "Exposes learners to the latest <strong>AI tools, workflows, and practical applications</strong>",
        "Supports flexible delivery through formats including <strong>MOOCs and live online cohorts</strong>",
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/8crgkshe_Frame%202147258425%20%281%29.png",
    },
    {
      id: 3,
      title: "No-Code to High-Code Workflows",
      description:
        "Flexible learning environments that take users from intuitive drag-and-drop experimentation to advanced code-based model development, supporting diverse learner profiles and varying levels of technical expertise.",
      bullets: [
        "Helps learners begin with <strong>no-code tools</strong> to understand AI concepts without programming barriers",
        "Supports progression to <strong>low-code and high-code environments</strong> as learners build confidence",
        "Gives learners <strong>access to custom datasets, agentic AI labs, and guided workflows</strong> across different levels of technical confidence",
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/5k5yuozp_Copy%20of%20benefits%20full%20screen%203.png",
    },
    {
      id: 4,
      title: "Industry Simulations",
      description:
        "Role-based simulations across Life Sciences, Retail, Ecommerce, Manufacturing, and Financial Services, helping learners understand how AI transforms these sectors and apply it to solve real-world problems.",
      bullets: [
        "Gives learners <strong>exposure to role-based scenarios</strong> that reflect how AI is used in real work settings",
        "Helps learners <strong>solve practical problems across industries</strong> using AI tools and workflows",
        "Builds understanding of <strong>industry-specific use cases, decisions, and challenges</strong>",
        "Enables applied <strong>learning through simulated environments</strong> that reflect real-world contexts",
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/jqwdb85h_Frame%202147258425%20%283%29.png",
    },
    {
      id: 5,
      title: "Assessment & Tracking",
      description:
        "Integrated assessment systems with quizzes, simulations, and analytics that track learner progress, measure outcomes, and provide actionable insights for both instructors and institutions.",
      bullets: [
        "Enables learners to <strong>complete quizzes, assignments, and simulations</strong> to test understanding",
        "Helps track <strong>progress across courses, labs, and learning milestones</strong>",
        "<strong>Identifies strengths, gaps,</strong> and areas that need improvement",
        "Gives instructors and institutions clear visibility into learner performance",
      ],
      imageUrl: "https://customer-assets.emergentagent.com/job_laughing-sinoussi-3/artifacts/4vnhivs1_Frame%202147258425%20%284%29.png",
    },
  ],
};

export const experienceSection = {
  heading: "Experience Elevate in Action",
  subtitle:
    "See how elevante.ai transforms AI learning with interactive labs, guided courses, and real-world simulations.",
  cta: "Watch our product",
};

export const ctaBanner = {
  heading: "Ready to bring AI to your institution?",
  ctaPrimary: { label: "Book a demo", href: "#demo" },
  ctaSecondary: { label: "Request info", href: "#info" },
};

export const footerData = {
  logo: "elevante.ai",
  links: [
    { label: "About", href: "/about" },
    { label: "For Educational Institution", href: "/educational-institution" },
    { label: "For Enterprises", href: "/for-enterprises" },
    { label: "Contact Us", action: "book-demo" },
  ],
  copyright: "\u00a9 2026 elevante.ai | All rights reserved.",
  legal: [
    { label: "Terms & Conditions", href: "/terms" },
    { label: "Privacy Policy", href: "/privacy" },
  ],
};
